import React from 'react';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { FadeTransition, ScaleTransition } from './WizardTransition';
import { getProgressIndicatorClasses } from '../utils/responsiveUtils';

/**
 * Wizard Progress Indicator Component
 * Shows the current step and progress through the 3-step wizard
 */
const WizardProgressIndicator = ({ 
  currentStep = 1, 
  steps = [
    { number: 1, title: 'Basic Configuration', description: 'Name, language, and timing settings' },
    { number: 2, title: 'Voice Configuration', description: 'Voice selection and quality settings' },
    { number: 3, title: 'AI Configuration', description: 'System prompt and behavior' }
  ],
  className = '',
  animated = true,
  showProgress = true
}) => {
  const { theme } = useTheme();
  const progressClasses = getProgressIndicatorClasses();

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (stepNumber, status) => {
    if (status === 'completed') {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    }
    return stepNumber;
  };

  const getStepClasses = (status) => {
    const baseClasses = `${progressClasses.step} rounded-full text-sm font-medium transition-all duration-300 ${
      animated ? 'transform hover:scale-110' : ''
    }`;
    
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-teal-600 text-white shadow-lg ${
          animated ? 'animate-pulse' : ''
        }`;
      case 'current':
        return `${baseClasses} bg-teal-600 text-white ring-4 shadow-lg ${
          theme === 'dark' ? 'ring-teal-600/20' : 'ring-teal-600/20'
        } ${animated ? 'animate-pulse' : ''}`;
      case 'upcoming':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-gray-700 text-gray-400 border-2 border-gray-600' 
            : 'bg-gray-200 text-gray-500 border-2 border-gray-300'
        }`;
      default:
        return baseClasses;
    }
  };

  const getConnectorClasses = (stepNumber) => {
    const isCompleted = stepNumber < currentStep;
    return `${progressClasses.connector} rounded-full transition-all duration-500 ${
      isCompleted 
        ? 'bg-teal-600 shadow-sm' 
        : theme === 'dark' 
          ? 'bg-gray-700' 
          : 'bg-gray-200'
    }`;
  };

  return (
    <div className={`wizard-progress-indicator ${className}`}>
      {/* Desktop Progress Indicator */}
      <div className={progressClasses.desktop}>
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => {
            const status = getStepStatus(step.number);
            const isLast = index === steps.length - 1;
            
            return (
              <React.Fragment key={step.number}>
                <ScaleTransition show={true}>
                  <div className="flex flex-col items-center">
                    <div 
                      className={getStepClasses(status)}
                      role="progressbar"
                      aria-valuenow={currentStep}
                      aria-valuemin={1}
                      aria-valuemax={steps.length}
                      aria-label={`Step ${step.number}: ${step.title}`}
                    >
                      {getStepIcon(step.number, status)}
                    </div>
                    <div className="mt-3 text-center">
                      <div className={`${progressClasses.label} font-medium transition-colors duration-200 ${
                        status === 'current' 
                          ? theme === 'dark' ? 'text-white' : 'text-gray-900'
                          : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {step.title}
                      </div>
                      <div className={`text-xs mt-1 max-w-32 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {step.description}
                      </div>
                    </div>
                  </div>
                </ScaleTransition>
                
                {!isLast && (
                  <div className={getConnectorClasses(step.number)} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* Progress Bar */}
        {showProgress && (
          <FadeTransition show={true}>
            <div className={`w-full rounded-full h-2 mb-8 overflow-hidden ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            }`}>
              <div 
                className={`bg-teal-600 h-2 rounded-full transition-all duration-500 ease-out shadow-sm ${
                  animated ? 'animate-pulse' : ''
                }`}
                style={{ 
                  width: `${(currentStep / steps.length) * 100}%`,
                  transform: `translateX(${animated ? '0' : '-100%'})`
                }}
                role="progressbar"
                aria-valuenow={currentStep}
                aria-valuemin={1}
                aria-valuemax={steps.length}
                aria-label={`Progress: ${Math.round((currentStep / steps.length) * 100)}% complete`}
              />
            </div>
          </FadeTransition>
        )}
      </div>

      {/* Enhanced Mobile Progress Indicator */}
      <div className={`${progressClasses.mobile} mb-8`}>
        <FadeTransition show={true}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                Step {currentStep} of {steps.length}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {Math.round((currentStep / steps.length) * 100)}% Complete
              </div>
            </div>
            
            {showProgress && (
              <div className={`w-full rounded-full h-3 mb-4 overflow-hidden ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <div 
                  className={`bg-teal-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm relative ${
                    animated ? 'animate-pulse' : ''
                  }`}
                  style={{ width: `${(currentStep / steps.length) * 100}%` }}
                  role="progressbar"
                  aria-valuenow={currentStep}
                  aria-valuemin={1}
                  aria-valuemax={steps.length}
                  aria-label={`Progress: ${Math.round((currentStep / steps.length) * 100)}% complete`}
                >
                  {/* Progress indicator glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse" />
                </div>
              </div>
            )}
            
            <ScaleTransition show={true}>
              <div className="text-center">
                <div className="text-base font-semibold text-gray-900 dark:text-white transition-colors duration-200 mb-1">
                  {steps[currentStep - 1]?.title}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-200">
                  {steps[currentStep - 1]?.description}
                </div>
              </div>
            </ScaleTransition>

            {/* Mobile step indicators */}
            <div className="flex justify-center space-x-2 mt-4">
              {steps.map((step, index) => {
                const status = getStepStatus(step.number);
                return (
                  <div
                    key={step.number}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      status === 'completed'
                        ? 'bg-teal-600 scale-110'
                        : status === 'current'
                          ? 'bg-teal-600 scale-125 ring-2 ring-teal-600/20'
                          : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    role="progressbar"
                    aria-label={`Step ${step.number}: ${status}`}
                  />
                );
              })}
            </div>
          </div>
        </FadeTransition>
      </div>
    </div>
  );
};

export default WizardProgressIndicator;
